import { useOfflineGatedTooltip } from '@/components/offline-recording/use-offline-gated-tooltip';
import {
  IS_EVIDENCE_LEADERBOARD_ENABLED,
  IS_SIDEBAR_REFERRAL_BUTTON_ENABLED,
} from '@/features/flags';
import { useIsFreeUser } from '@/hooks/useIsFreeUser';
import useReverseTrial from '@/hooks/useReverseTrial';
import { trackButtonClicked } from '@/lib/analytics/scribe/button';
import { isReferralProgramAvailableForLocale } from '@/utils/i18n/referralProgramLocale';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@heidi/design-system/page-shell';
import {
  isAffiliateLinkModalOpenAtom,
  isEvidenceReferralModalOpenAtom,
} from '@heidi/growth';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { Gift } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useEvidenceNavEnabled } from './hooks/useEvidenceNavEnabled';
import { useIsRibbonRail } from './hooks/useIsRibbonRail';
import { NavItem } from './sidebar';
import {
  useFooterRailCollapsible,
  type FooterMoreRowDescriptor,
} from './sidebar/footer-rail/footer-rail-context';

interface ReferralButtonProps {
  onOpen?: () => void;
}

export function ReferralButton({ onOpen }: ReferralButtonProps) {
  const { t, locale } = useTranslations();
  const isRibbonRail = useIsRibbonRail();
  const { isOfflineGated, gatedTooltip } = useOfflineGatedTooltip();
  const setIsAffiliateLinkModalOpen = useSetAtom(isAffiliateLinkModalOpenAtom);
  const setIsEvidenceReferralModalOpen = useSetAtom(isEvidenceReferralModalOpenAtom);
  const { isFreeUser } = useIsFreeUser();
  const {
    isPracticeTrialUser,
    isClinicianTrialUser,
    isEvidenceTrialUser,
    isProTrialUser,
    isClinicianPracticeTrialUser,
    isInReverseTrial,
    isFakeClinicianTrial,
  } = useReverseTrial();
  const { showEvidenceFocusedNav } = useEvidenceNavEnabled();

  const isTrialUser =
    isPracticeTrialUser ||
    isClinicianTrialUser ||
    isEvidenceTrialUser ||
    isProTrialUser ||
    isClinicianPracticeTrialUser ||
    isInReverseTrial ||
    isFakeClinicianTrial;

  // Don't show if Evidence referral CTA is already visible for Evidence signup users
  const hasEvidenceReferralCta =
    showEvidenceFocusedNav && IS_EVIDENCE_LEADERBOARD_ENABLED();

  const shouldRender =
    IS_SIDEBAR_REFERRAL_BUTTON_ENABLED() &&
    isReferralProgramAvailableForLocale(locale) &&
    !isFreeUser &&
    !isTrialUser &&
    !hasEvidenceReferralCta;

  const handleClick = useCallback(() => {
    trackButtonClicked({
      screenName: ScreenName.Sidenav,
      action: ButtonAction.SidebarReferralButtonClicked,
      label: 'Refer and earn',
    });
    if (showEvidenceFocusedNav) {
      setIsEvidenceReferralModalOpen(true);
    } else {
      setIsAffiliateLinkModalOpen(true);
    }
    onOpen?.();
  }, [
    showEvidenceFocusedNav,
    setIsEvidenceReferralModalOpen,
    setIsAffiliateLinkModalOpen,
    onOpen,
  ]);

  const referLabel = t('sidenav.referAndEarnPaid');
  const referDescriptor = useMemo<FooterMoreRowDescriptor>(
    () => ({
      id: 'refer',
      label: referLabel,
      icon: <Gift />,
      testId: 'sidebar-referral-button',
      onSelect: handleClick,
    }),
    [referLabel, handleClick]
  );
  const isReferCollapsed = useFooterRailCollapsible({
    id: 'refer',
    visible: Boolean(shouldRender) && isRibbonRail,
    pinned: false,
    descriptor: referDescriptor,
  });

  if (!shouldRender || isReferCollapsed) {
    return null;
  }

  // Ribbon rail: bare item — the shared footer <ul> is owned by SideNavFooter.
  if (isRibbonRail) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          layout="iconOnly"
          disabled={isOfflineGated}
          tooltip={gatedTooltip(t('sidenav.referAndEarnPaid'))}
          onClick={isOfflineGated ? undefined : handleClick}
          aria-label={t('sidenav.referAndEarnPaid')}
          data-testid="sidebar-referral-button"
        >
          <Gift
            className={isOfflineGated ? 'text-text-tertiary' : 'text-tier-pro-text'}
          />
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenu>
      <NavItem
        label={t('sidenav.referAndEarnPaid')}
        icon={
          <Gift
            className={
              isOfflineGated ? 'size-4 text-text-tertiary' : 'size-4 text-tier-pro-text'
            }
          />
        }
        onClick={handleClick}
        variant={isOfflineGated ? 'default' : 'emphasis'}
        testId="sidebar-referral-button"
      />
    </SidebarMenu>
  );
}
